import * as XLSX from "xlsx";

import type { IDynamicCsvColumns } from "@ryde/controls";
import {
  FIELD_TYPE_IDS,
  JsonTypeEnum,
  safeParseNumericStringOrUndefined,
  type IGenericExportExcelTransformerCtx,
  type TGenericExportExcelCellTransformer,
  type TGenericExportExcelFieldMapTransformer,
  type TGridFieldValues,
} from "@ryde/shared";

// Excel "Text" number format. Keeps a cell as text instead of letting Excel
// coerce a numeric-looking string into a number.
const EXCEL_TEXT_FORMAT = "@";

/**
 * Forces a cell to Excel "Text", so numeric-looking strings such as phone
 * numbers (e.g. `972684456`) are preserved verbatim and never reformatted into
 * scientific notation when the cell is edited.
 */
const toTextCell = (cell: XLSX.CellObject): void => {
  cell.t = "s";
  cell.v = String(cell.v ?? "");
  cell.z = EXCEL_TEXT_FORMAT;
};

/**
 * Turns a numeric-looking cell into a real Excel number cell, so the column can
 * be summed. The csv cell transformers always hand back strings, and
 * `json_to_sheet` types every cell from the JS value it receives, so without
 * this a price column reaches Excel as text and `SUM` over it returns 0.
 *
 * Values that are not purely numeric — `NO_VALUE_DISPLAY`, anything already
 * formatted with a currency symbol or a thousands separator — are left as text
 * rather than risking a wrong numeric reading.
 */
const toNumberCell = (cell: XLSX.CellObject): void => {
  const numericValue = safeParseNumericStringOrUndefined(cell.v);

  if (numericValue === undefined) {
    return;
  }

  cell.t = "n";
  cell.v = numericValue;
  // Drop the cached text rendering json_to_sheet may have produced for the
  // string, otherwise Excel shows it instead of the number, and any inherited
  // number format — a leftover `EXCEL_TEXT_FORMAT` would keep Excel treating
  // the cell as text even though its type is now numeric.
  delete cell.w;
  delete cell.z;
};

const genericTransformer: TGenericExportExcelCellTransformer = ({
  cell,
  gridCustomField,
}: IGenericExportExcelTransformerCtx) => {
  switch (gridCustomField.jsonType) {
    case JsonTypeEnum.String: {
      return toTextCell(cell);
    }
    case JsonTypeEnum.Number:
    case JsonTypeEnum.Boolean:
    case JsonTypeEnum.Array:
    case JsonTypeEnum.Object:
    case JsonTypeEnum.Date: {
      break;
    }
    default: {
      break;
    }
  }
};

const phoneTransformer: TGenericExportExcelCellTransformer = ({
  cell,
}: IGenericExportExcelTransformerCtx) => {
  toTextCell(cell);
};

/**
 * Columns holding a measurable quantity (prices, distances, durations, counts).
 * Unlike the id-like numbers that `genericTransformer` leaves alone, these are
 * the columns users aggregate in Excel, so they must arrive as numbers.
 */
const numberTransformer: TGenericExportExcelCellTransformer = ({
  cell,
}: IGenericExportExcelTransformerCtx) => {
  toNumberCell(cell);
};

/**
 * Maps a grid field type id to the Excel cell transformer applied to its
 * column. Field types absent from the map keep the type/format Excel infers
 * from the value — the same fallback strategy `csvTransformerControlsMap` uses.
 *
 * To give another field type custom Excel rendering, add an entry here.
 */
export const xlsxCellTransformerMap: TGenericExportExcelFieldMapTransformer = {
  [FIELD_TYPE_IDS.GRID.address]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.booking_status]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.checkbox]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.course_schedule]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.course_stations]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.csv_list]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.currency]: numberTransformer,
  [FIELD_TYPE_IDS.GRID.date]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.date_range]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.date_with_indicator]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.datetime]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.datetime_range]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.direction]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.dropdown_bound_text]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.email]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.number]: numberTransformer,
  [FIELD_TYPE_IDS.GRID.phone]: phoneTransformer,
  [FIELD_TYPE_IDS.GRID.ride_status]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.switch]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.switch_alternative]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.switch_confirm]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.tags]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.text]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.time]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.time_range]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.url]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.vehicle_occupancy]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.price_rate_pricing]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.ride_start_end_datetime]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.complaint_status]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.action_button]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.data_upload_run_button]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.notification_priority]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.date_countdown]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.notification_grid_content]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.entity_name]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.entity_link]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.open_entity_form]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.feedback_status]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.ride_editable_dropdown_bound]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.plain_text]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.feedbacks_count]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.ride_passengers_csv_list]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.currency_discount]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.ride_ml_path_indicator]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.hidden_field]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.warnings_display]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.table_availability]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.live_map_arrival_delay]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.gps_indicator]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.ride_editable_docks_dropdown]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.datetime_compact_readonly]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.boolean_image_icon]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.production_chain]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.live_map_delay_status]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.hub_stations_grid_template]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.booking_hub_grid_template]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.json_view]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.live_map_ride_start_status]: genericTransformer,
  [FIELD_TYPE_IDS.GRID.ride_route_completion_status]: genericTransformer,
};

/**
 * Applies the field-type-specific Excel formatting to a worksheet produced by
 * `XLSX.utils.json_to_sheet`.
 *
 * @param columns - data columns in worksheet order; the id column occupies
 *   index 0, so `columns[i]` lives in worksheet column `i + 1`.
 * @param rowCount - number of data rows (the header occupies row 0).
 */
export const applyXlsxCellTransformers = (
  worksheet: XLSX.WorkSheet,
  columns: IDynamicCsvColumns[],
  rowCount: number,
): void => {
  columns.forEach((column, columnIndex) => {
    const { gridFieldTypeId } = column.meta;

    if (!gridFieldTypeId) {
      return;
    }

    const transformCell =
      xlsxCellTransformerMap[gridFieldTypeId as TGridFieldValues] ??
      genericTransformer;

    // +1 to skip the leading id column.
    const worksheetColumn = columnIndex + 1;

    for (let row = 1; row <= rowCount; row++) {
      const cellRef = XLSX.utils.encode_cell({ c: worksheetColumn, r: row });
      const cell = worksheet[cellRef];

      if (!cell) {
        continue;
      }

      if (cell.v === undefined || cell.v === null || cell.v === "") {
        continue;
      }

      transformCell({ cell, gridCustomField: column.gridCustomField });
    }
  });
};
